import { useState, useEffect, useMemo } from 'react';
import { getPrestataires, createPrestataire, updatePrestataire, deletePrestataire } from '../services/airtable';
import { useToast } from '../contexts/ToastContext';

const EMPTY_FORM = { nom: '', statut: 'Actif', email: '', siret: '', adresse: '', code_postal: '', ville: '' };

export function usePrestatairesManagement() {
  const { showToast } = useToast();

  const [prestataires, setPrestataires] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [searchTerm, setSearchTerm]     = useState('');

  const [showModal, setShowModal]       = useState(false);
  const [modalMode, setModalMode]       = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting]     = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete]       = useState(null);
  const [deleting, setDeleting]               = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getPrestataires();
      // Normalise depuis le format { id, fields: { Nom, Statut, Email, SIRET, Adresse } }
      setPrestataires((data || []).map(p => ({
        id:      p.id,
        nom:     p.fields?.Nom     || '',
        statut:  p.fields?.Statut  || 'Actif',
        email:   p.fields?.Email   || '',
        siret:   p.fields?.SIRET   || '',
        adresse: p.fields?.Adresse || '',
        code_postal: p.fields?.CodePostal || '',
        ville:       p.fields?.Ville      || '',
      })));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredPrestataires = useMemo(() => {
    if (!searchTerm) return prestataires;
    const q = searchTerm.toLowerCase();
    return prestataires.filter(p =>
      p.nom.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      p.siret.toLowerCase().includes(q)
    );
  }, [prestataires, searchTerm]);

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setFormData(mode === 'edit' && item
      ? {
          nom: item.nom, statut: item.statut, email: item.email, siret: item.siret,
          adresse: item.adresse, code_postal: item.code_postal, ville: item.ville
        }
      : EMPTY_FORM
    );
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await createPrestataire(formData);
        showToast('Prestataire créé avec succès', 'success');
      } else {
        await updatePrestataire(selectedItem.id, formData);
        showToast('Prestataire modifié avec succès', 'success');
      }
      await load();
      handleCloseModal();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deletePrestataire(itemToDelete.id);
      showToast('Prestataire supprimé avec succès', 'success');
      await load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  return {
    prestataires, filteredPrestataires, loading, error,
    searchTerm, setSearchTerm,
    showModal, modalMode, formData, setFormData, submitting,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, itemToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
  };
}
